import { useCallback, useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { canManageStaff, initializeSession } from '../../lib/auth'
import type { Lang } from '../../lib/i18n'
import { CommandHeader } from '../../components/CommandHeader'
import {
  createStaff,
  deactivateStaff,
  fetchGrantableRoles,
  fetchStaff,
  reactivateStaff,
  resetStaffPassword,
  setStaffRoles,
  signOutEverywhere,
  StaffApiError,
  type GrantableRole,
  type StaffRow,
} from '../../lib/staff-api'
import { st } from './staff-i18n'
import { PasswordInput } from '../../components/PasswordInput'

interface Props {
  lang: Lang
  onLangToggle: () => void
  isOnline: boolean
}

type AuthState = 'checking' | 'ok' | 'redirect'
type LoadState = 'loading' | 'ready' | 'error'

interface RowFormState {
  newPassword: string
  confirmPassword: string
  submitting: boolean
  error: string | null
}

function emptyForm(): RowFormState {
  return { newPassword: '', confirmPassword: '', submitting: false, error: null }
}

// R413 (c) - the create form and the per-row role picker.
interface CreateFormState {
  name: string
  username: string
  password: string
  roleIds: string[]
  submitting: boolean
  error: string | null
}

function emptyCreateForm(): CreateFormState {
  return { name: '', username: '', password: '', roleIds: [], submitting: false, error: null }
}

interface RolesFormState {
  // null until the person touches a box: the selection is then derived from row.roles.
  roleIds: string[] | null
  submitting: boolean
  error: string | null
}

type StaffKey = Parameters<typeof st>[1]

/** The role_<slug> label when that key exists, else the raw slug. */
function roleLabel(lang: Lang, slug: string): string {
  const label: unknown = st(lang, `role_${slug}` as StaffKey)
  return typeof label === 'string' ? label : slug
}

function toggleId(ids: string[], id: string): string[] {
  return ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]
}

// bench-STAFF-PASSWORD-RESET-SCREEN.CB-B §1.8. Owner/architect reset a staff login's
// password under rank rules (§1 BUILD) — canReset arrives per-row from the server, already
// computed from mayResetPassword, so the screen never offers an action the service would
// refuse.
export function StaffScreen({ lang, onLangToggle, isOnline }: Props) {
  const [authState, setAuthState] = useState<AuthState>('checking')
  const [rows, setRows] = useState<StaffRow[]>([])
  const [loadState, setLoadState] = useState<LoadState>('loading')
  const [openRowId, setOpenRowId] = useState<string | null>(null)
  const [forms, setForms] = useState<Record<string, RowFormState>>({})
  const [successMsg, setSuccessMsg] = useState<Record<string, string>>({})
  // bench-STAFF-LOGIN-OFF-AND-OWN-PASSWORD.CB-B §1.7 — switch off/on per row. 'confirm' only
  // happens on the way to switching OFF (the bench asks for confirmation there, not on
  // switching back on).
  const [switchState, setSwitchState] = useState<Record<string, 'confirm' | 'submitting'>>({})
  const [switchError, setSwitchError] = useState<Record<string, string | null>>({})
  // bench-THE-TILL-REMEMBERS-THIS-DEVICE.CB-A.a (R276) - "Sign out all devices" per row: asks
  // first ('confirm'), then posts ('submitting'); the outcome line is the done/failed string.
  const [signOutState, setSignOutState] = useState<Record<string, 'confirm' | 'submitting'>>({})
  const [signOutResult, setSignOutResult] = useState<Record<string, 'done' | 'failed'>>({})
  // R413 (c) - grantable roles (GET /api/auth/roles), loaded when a picker first opens.
  const [roles, setRoles] = useState<GrantableRole[]>([])
  const [rolesState, setRolesState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle')
  const [createOpen, setCreateOpen] = useState(false)
  const [createForm, setCreateForm] = useState<CreateFormState>(emptyCreateForm())
  const [createSuccess, setCreateSuccess] = useState<string | null>(null)
  const [rolesOpenId, setRolesOpenId] = useState<string | null>(null)
  const [rolesForm, setRolesForm] = useState<RolesFormState>({ roleIds: null, submitting: false, error: null })

  useEffect(() => {
    // §1.8 — the same gate shape as CockpitScreen.tsx:14-21, but on canManageStaff(), which
    // does NOT consult isReadOnly: a pure architect (read-only everywhere else) still sees
    // this screen.
    initializeSession().then(() => {
      setAuthState(canManageStaff() ? 'ok' : 'redirect')
    })
  }, [])

  const load = useCallback(() => {
    setLoadState('loading')
    fetchStaff()
      .then((data) => {
        setRows(data)
        setLoadState('ready')
      })
      .catch(() => {
        // The rejection itself already dispatched hop:auth-required on a 401 (secureFetch,
        // inside fetchStaff) — this screen just shows the load-failed state.
        setLoadState('error')
      })
  }, [])

  useEffect(() => {
    if (authState === 'ok') load()
  }, [authState, load])

  const loadRoles = useCallback(() => {
    setRolesState('loading')
    fetchGrantableRoles()
      .then((data) => {
        setRoles(data)
        setRolesState('ready')
      })
      .catch(() => setRolesState('error'))
  }, [])

  if (authState === 'checking') return null
  if (authState === 'redirect') return <Navigate to="/login" replace />

  const dir = lang === 'ar' ? 'rtl' : 'ltr'

  function openRow(id: string) {
    setOpenRowId(id)
    setForms((prev) => ({ ...prev, [id]: emptyForm() }))
    setSuccessMsg((prev) => {
      if (!(id in prev)) return prev
      const next = { ...prev }
      delete next[id]
      return next
    })
  }

  function closeRow(id: string) {
    setOpenRowId((cur) => (cur === id ? null : cur))
  }

  function updateForm(id: string, patch: Partial<RowFormState>) {
    setForms((prev) => ({ ...prev, [id]: { ...(prev[id] ?? emptyForm()), ...patch } }))
  }

  function failureMessageFor(err: unknown): string {
    if (err instanceof StaffApiError) {
      if (err.status === 400) return st(lang, 'error400')
      if (err.status === 403) return st(lang, 'error403')
      if (err.status === 404) return st(lang, 'error404')
      return st(lang, 'errorOther')
    }
    // A thrown non-StaffApiError here is a network failure (fetch itself rejected) — the
    // offline case, caught mid-submit rather than pre-empted by the disabled action below.
    return st(lang, 'errorOffline')
  }

  async function submitReset(row: StaffRow) {
    const form = forms[row.id] ?? emptyForm()
    updateForm(row.id, { submitting: true, error: null })
    try {
      await resetStaffPassword(row.id, form.newPassword)
      // Success: both inputs cleared, nothing kept in state (the row's form entry is
      // dropped entirely, not just blanked) — §1.8.
      setForms((prev) => {
        const next = { ...prev }
        delete next[row.id]
        return next
      })
      setOpenRowId((cur) => (cur === row.id ? null : cur))
      setSuccessMsg((prev) => ({ ...prev, [row.id]: st(lang, 'resetSuccess').replace('{name}', row.name) }))
    } catch (err) {
      // Failure: the inputs are KEPT for the person to see (unlike success) — only the
      // pending flag clears and an error message appears.
      updateForm(row.id, { submitting: false, error: failureMessageFor(err) })
    }
  }

  function askSwitchOff(id: string) {
    setSwitchState((prev) => ({ ...prev, [id]: 'confirm' }))
    setSwitchError((prev) => ({ ...prev, [id]: null }))
  }

  function cancelSwitchOff(id: string) {
    setSwitchState((prev) => {
      const next = { ...prev }
      delete next[id]
      return next
    })
    setSwitchError((prev) => ({ ...prev, [id]: null }))
  }

  async function confirmSwitchOff(row: StaffRow) {
    setSwitchState((prev) => ({ ...prev, [row.id]: 'submitting' }))
    try {
      await deactivateStaff(row.id)
      setSwitchState((prev) => {
        const next = { ...prev }
        delete next[row.id]
        return next
      })
      load()
    } catch (err) {
      setSwitchState((prev) => ({ ...prev, [row.id]: 'confirm' }))
      setSwitchError((prev) => ({ ...prev, [row.id]: failureMessageFor(err) }))
    }
  }

  function askSignOut(id: string) {
    setSignOutState((prev) => ({ ...prev, [id]: 'confirm' }))
    setSignOutResult((prev) => {
      const next = { ...prev }
      delete next[id]
      return next
    })
  }

  function cancelSignOut(id: string) {
    setSignOutState((prev) => {
      const next = { ...prev }
      delete next[id]
      return next
    })
  }

  async function confirmSignOut(row: StaffRow) {
    setSignOutState((prev) => ({ ...prev, [row.id]: 'submitting' }))
    let outcome: 'done' | 'failed' = 'done'
    try {
      await signOutEverywhere(row.id)
    } catch {
      outcome = 'failed'
    }
    setSignOutState((prev) => {
      const next = { ...prev }
      delete next[row.id]
      return next
    })
    setSignOutResult((prev) => ({ ...prev, [row.id]: outcome }))
  }

  async function switchOn(row: StaffRow) {
    setSwitchState((prev) => ({ ...prev, [row.id]: 'submitting' }))
    setSwitchError((prev) => ({ ...prev, [row.id]: null }))
    try {
      await reactivateStaff(row.id)
      setSwitchState((prev) => {
        const next = { ...prev }
        delete next[row.id]
        return next
      })
      load()
    } catch (err) {
      setSwitchState((prev) => {
        const next = { ...prev }
        delete next[row.id]
        return next
      })
      setSwitchError((prev) => ({ ...prev, [row.id]: failureMessageFor(err) }))
    }
  }

  function ensureRoles() {
    if (rolesState === 'idle' || rolesState === 'error') loadRoles()
  }

  function openCreate() {
    setCreateOpen(true)
    setCreateForm(emptyCreateForm())
    setCreateSuccess(null)
    ensureRoles()
  }

  function updateCreate(patch: Partial<CreateFormState>) {
    setCreateForm((prev) => ({ ...prev, ...patch }))
  }

  async function submitCreate() {
    const form = createForm
    if (form.password.length < 8) {
      updateCreate({ error: st(lang, 'errorTooShort') })
      return
    }
    // No role picked: say so and never call the API.
    if (form.roleIds.length === 0) {
      updateCreate({ error: st(lang, 'errorNoRole') })
      return
    }
    updateCreate({ submitting: true, error: null })
    try {
      await createStaff({
        name: form.name,
        username: form.username,
        initialPassword: form.password,
        roleIds: form.roleIds,
      })
      setCreateOpen(false)
      setCreateForm(emptyCreateForm())
      setCreateSuccess(st(lang, 'createSuccess').replace('{name}', form.name))
      load()
    } catch (err) {
      const message =
        err instanceof StaffApiError && err.status === 409 ? st(lang, 'errorUsernameTaken') : failureMessageFor(err)
      updateCreate({ submitting: false, error: message })
    }
  }

  function selectedRoleIds(row: StaffRow): string[] {
    if (rolesForm.roleIds !== null) return rolesForm.roleIds
    // row.roles holds slugs; the PATCH takes ids, mapped through the loaded roles list.
    return roles.filter((r) => row.roles.includes(r.slug)).map((r) => r.id)
  }

  function openRoles(row: StaffRow) {
    setRolesOpenId(row.id)
    setRolesForm({ roleIds: null, submitting: false, error: null })
    setSuccessMsg((prev) => {
      if (!(row.id in prev)) return prev
      const next = { ...prev }
      delete next[row.id]
      return next
    })
    ensureRoles()
  }

  async function submitRoles(row: StaffRow) {
    const roleIds = selectedRoleIds(row)
    if (roleIds.length === 0) {
      setRolesForm((prev) => ({ ...prev, error: st(lang, 'errorNoRole') }))
      return
    }
    setRolesForm((prev) => ({ ...prev, submitting: true, error: null }))
    try {
      await setStaffRoles(row.id, roleIds)
      setRolesOpenId(null)
      setRolesForm({ roleIds: null, submitting: false, error: null })
      setSuccessMsg((prev) => ({ ...prev, [row.id]: st(lang, 'rolesSaved').replace('{name}', row.name) }))
      load()
    } catch (err) {
      setRolesForm((prev) => ({ ...prev, submitting: false, error: failureMessageFor(err) }))
    }
  }

  function renderRolePicker(testIdPrefix: string, selected: string[], onToggle: (id: string) => void) {
    return (
      <fieldset className="space-y-1" data-testid={`${testIdPrefix}-roles`}>
        <legend className="block text-xs font-medium text-foreground mb-1">{st(lang, 'fieldRoles')}</legend>
        {(rolesState === 'loading' || rolesState === 'idle') && (
          <p className="text-xs text-muted-foreground">{st(lang, 'loading')}</p>
        )}
        {rolesState === 'error' && (
          <div
            className="rounded-md bg-destructive/10 border border-destructive/30 px-3 py-2 text-xs text-destructive flex items-center justify-between"
            data-testid={`${testIdPrefix}-roles-error`}
          >
            <span>{st(lang, 'errorRolesLoad')}</span>
            <button
              type="button"
              onClick={loadRoles}
              data-testid={`${testIdPrefix}-roles-retry`}
              className="font-semibold underline"
            >
              {st(lang, 'retry')}
            </button>
          </div>
        )}
        {rolesState === 'ready' &&
          roles.map((role) => (
            <label key={role.id} className="flex items-center gap-2 text-sm text-foreground min-h-[44px]">
              <input
                type="checkbox"
                data-testid={`${testIdPrefix}-role-${role.slug}`}
                checked={selected.includes(role.id)}
                onChange={() => onToggle(role.id)}
                className="h-5 w-5 rounded border-input"
              />
              <span>{roleLabel(lang, role.slug)}</span>
            </label>
          ))}
      </fieldset>
    )
  }

  return (
    <div dir={dir} className="min-h-screen bg-background font-sans">
      <CommandHeader title={st(lang, 'title')} lang={lang} onLangToggle={onLangToggle} isOnline={isOnline} />

      <main className="px-4 py-6 max-w-2xl mx-auto pb-10">
        {canManageStaff() && (
          <section className="mb-4" data-testid="staff-create">
            {createSuccess && (
              <p className="mb-2 text-sm text-green-700" data-testid="staff-create-success">
                {createSuccess}
              </p>
            )}

            {!createOpen && (
              <>
                <button
                  type="button"
                  onClick={openCreate}
                  disabled={!isOnline}
                  data-testid="staff-create-open"
                  className="rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px]"
                >
                  {st(lang, 'createOpen')}
                </button>
                {!isOnline && (
                  <p className="text-xs text-amber-600 mt-1" data-testid="staff-create-offline">
                    {st(lang, 'needsConnection')}
                  </p>
                )}
              </>
            )}

            {createOpen && (
              <form
                className="bg-card rounded-lg border border-border p-4 space-y-2"
                data-testid="staff-create-form"
                onSubmit={(e) => {
                  e.preventDefault()
                  if (!createForm.submitting && isOnline) void submitCreate()
                }}
              >
                <h2 className="font-semibold text-foreground">{st(lang, 'createTitle')}</h2>
                {createForm.error && (
                  <div
                    className="rounded-md bg-destructive/10 border border-destructive/30 px-3 py-2 text-xs text-destructive"
                    data-testid="staff-create-error"
                  >
                    {createForm.error}
                  </div>
                )}
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1">{st(lang, 'fieldName')}</label>
                  <input
                    type="text"
                    data-testid="staff-create-name"
                    value={createForm.name}
                    onChange={(e) => updateCreate({ name: e.target.value })}
                    className="w-full rounded-md border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1">{st(lang, 'fieldUsername')}</label>
                  <input
                    type="text"
                    autoComplete="off"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    data-testid="staff-create-username"
                    value={createForm.username}
                    onChange={(e) => updateCreate({ username: e.target.value })}
                    className="w-full rounded-md border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1">
                    {st(lang, 'fieldStartPassword')}
                  </label>
                  <PasswordInput
                    lang={lang}
                    autoComplete="new-password"
                    data-testid="staff-create-password"
                    value={createForm.password}
                    onChange={(e) => updateCreate({ password: e.target.value })}
                    className="w-full rounded-md border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                {renderRolePicker('staff-create', createForm.roleIds, (id) =>
                  updateCreate({ roleIds: toggleId(createForm.roleIds, id) }),
                )}
                <p className="text-xs text-muted-foreground" data-testid="staff-create-hint">
                  {st(lang, 'createHint')}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="submit"
                    disabled={
                      createForm.submitting ||
                      !isOnline ||
                      createForm.name.trim() === '' ||
                      createForm.username.trim() === ''
                    }
                    data-testid="staff-create-submit"
                    className="rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors min-h-[44px]"
                  >
                    {createForm.submitting ? st(lang, 'creating') : st(lang, 'submitCreate')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setCreateOpen(false)}
                    disabled={createForm.submitting}
                    className="text-sm text-muted-foreground hover:text-foreground min-h-[44px]"
                  >
                    {st(lang, 'cancel')}
                  </button>
                </div>
              </form>
            )}
          </section>
        )}

        {loadState === 'loading' && (
          <p className="text-sm text-muted-foreground py-8 text-center">{st(lang, 'loading')}</p>
        )}

        {loadState === 'error' && (
          <div className="rounded-md bg-destructive/10 border border-destructive/30 px-4 py-3 text-sm text-destructive flex items-center justify-between">
            <span>{st(lang, 'errorLoading')}</span>
            <button type="button" onClick={load} className="font-semibold underline">
              {st(lang, 'retry')}
            </button>
          </div>
        )}

        {loadState === 'ready' && rows.length === 0 && (
          <p className="text-sm text-muted-foreground py-8 text-center">{st(lang, 'empty')}</p>
        )}

        {loadState === 'ready' && rows.length > 0 && (
          <ul className="space-y-3" data-testid="staff-list">
            {rows.map((row) => {
              const form = forms[row.id] ?? emptyForm()
              const isOpen = openRowId === row.id
              const canSubmit =
                form.newPassword.length >= 8 && form.newPassword === form.confirmPassword && !form.submitting

              return (
                <li
                  key={row.id}
                  className="bg-card rounded-lg border border-border p-4"
                  data-testid={`staff-row-${row.id}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-foreground">{row.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {row.username} · {row.roles.join(', ')}
                      </p>
                    </div>
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                        row.active ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'
                      }`}
                      data-testid={`staff-active-${row.id}`}
                    >
                      {st(lang, row.active ? 'badgeActive' : 'badgeInactive')}
                    </span>
                  </div>

                  {row.canReset && (
                    <div className="mt-3" data-testid={`staff-action-${row.id}`}>
                      {!isOpen && (
                        <>
                          <button
                            type="button"
                            onClick={() => openRow(row.id)}
                            disabled={!isOnline}
                            className="text-sm font-semibold text-foreground hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed min-h-[44px]"
                          >
                            {st(lang, 'actionReset')}
                          </button>
                          {!isOnline && (
                            <p className="text-xs text-amber-600 mt-1" data-testid={`staff-offline-${row.id}`}>
                              {st(lang, 'needsConnection')}
                            </p>
                          )}
                        </>
                      )}

                      {isOpen && (
                        <form
                          className="mt-2 space-y-2"
                          onSubmit={(e) => {
                            e.preventDefault()
                            if (canSubmit) void submitReset(row)
                          }}
                        >
                          {form.error && (
                            <div className="rounded-md bg-destructive/10 border border-destructive/30 px-3 py-2 text-xs text-destructive">
                              {form.error}
                            </div>
                          )}
                          <div>
                            <label className="block text-xs font-medium text-foreground mb-1">
                              {st(lang, 'fieldNewPassword')}
                            </label>
                            <PasswordInput
                              lang={lang}
                              autoComplete="new-password"
                              data-testid={`staff-new-password-${row.id}`}
                              value={form.newPassword}
                              onChange={(e) => updateForm(row.id, { newPassword: e.target.value })}
                              className="w-full rounded-md border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-foreground mb-1">
                              {st(lang, 'fieldConfirmPassword')}
                            </label>
                            <PasswordInput
                              lang={lang}
                              autoComplete="new-password"
                              data-testid={`staff-confirm-password-${row.id}`}
                              value={form.confirmPassword}
                              onChange={(e) => updateForm(row.id, { confirmPassword: e.target.value })}
                              className="w-full rounded-md border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="submit"
                              disabled={!canSubmit}
                              data-testid={`staff-submit-${row.id}`}
                              className="rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors min-h-[44px]"
                            >
                              {form.submitting ? st(lang, 'submitting') : st(lang, 'submitReset')}
                            </button>
                            <button
                              type="button"
                              onClick={() => closeRow(row.id)}
                              className="text-sm text-muted-foreground hover:text-foreground min-h-[44px]"
                            >
                              {st(lang, 'cancel')}
                            </button>
                          </div>
                        </form>
                      )}
                    </div>
                  )}

                  {row.canManage && (
                    <div className="mt-3" data-testid={`staff-switch-section-${row.id}`}>
                      {switchError[row.id] && (
                        <div
                          className="rounded-md bg-destructive/10 border border-destructive/30 px-3 py-2 text-xs text-destructive mb-2"
                          data-testid={`staff-switch-error-${row.id}`}
                        >
                          {switchError[row.id]}
                        </div>
                      )}

                      {!row.active && switchState[row.id] !== 'submitting' && (
                        <button
                          type="button"
                          onClick={() => void switchOn(row)}
                          disabled={!isOnline}
                          data-testid={`staff-switch-on-${row.id}`}
                          className="text-sm font-semibold text-foreground hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed min-h-[44px]"
                        >
                          {st(lang, 'actionSwitchOn')}
                        </button>
                      )}
                      {!row.active && switchState[row.id] === 'submitting' && (
                        <span className="text-sm text-muted-foreground">{st(lang, 'switchingOn')}</span>
                      )}

                      {row.active && !switchState[row.id] && (
                        <button
                          type="button"
                          onClick={() => askSwitchOff(row.id)}
                          disabled={!isOnline}
                          data-testid={`staff-switch-off-${row.id}`}
                          className="text-sm font-semibold text-foreground hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed min-h-[44px]"
                        >
                          {st(lang, 'actionSwitchOff')}
                        </button>
                      )}

                      {row.active && (switchState[row.id] === 'confirm' || switchState[row.id] === 'submitting') && (
                        <div className="space-y-2">
                          <p className="text-xs text-foreground" data-testid={`staff-switch-confirm-line-${row.id}`}>
                            {st(lang, 'confirmSwitchOff').replace('{name}', row.name)}
                          </p>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => void confirmSwitchOff(row)}
                              disabled={switchState[row.id] === 'submitting'}
                              data-testid={`staff-switch-confirm-${row.id}`}
                              className="rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors min-h-[44px]"
                            >
                              {switchState[row.id] === 'submitting' ? st(lang, 'switchingOff') : st(lang, 'confirmSwitch')}
                            </button>
                            <button
                              type="button"
                              onClick={() => cancelSwitchOff(row.id)}
                              disabled={switchState[row.id] === 'submitting'}
                              data-testid={`staff-switch-cancel-${row.id}`}
                              className="text-sm text-muted-foreground hover:text-foreground min-h-[44px]"
                            >
                              {st(lang, 'cancel')}
                            </button>
                          </div>
                        </div>
                      )}

                      {!signOutState[row.id] && (
                        <button
                          type="button"
                          onClick={() => askSignOut(row.id)}
                          disabled={!isOnline}
                          data-testid={`staff-signout-${row.id}`}
                          className="block text-sm font-semibold text-foreground hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed min-h-[44px]"
                        >
                          {st(lang, 'signOutEverywhere')}
                        </button>
                      )}
                      {signOutState[row.id] && (
                        <div className="space-y-2">
                          <p className="text-xs text-foreground" data-testid={`staff-signout-confirm-line-${row.id}`}>
                            {st(lang, 'signOutEverywhereConfirm')}
                          </p>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => void confirmSignOut(row)}
                              disabled={signOutState[row.id] === 'submitting'}
                              data-testid={`staff-signout-confirm-${row.id}`}
                              className="rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors min-h-[44px]"
                            >
                              {st(lang, 'signOutEverywhere')}
                            </button>
                            <button
                              type="button"
                              onClick={() => cancelSignOut(row.id)}
                              disabled={signOutState[row.id] === 'submitting'}
                              data-testid={`staff-signout-cancel-${row.id}`}
                              className="text-sm text-muted-foreground hover:text-foreground min-h-[44px]"
                            >
                              {st(lang, 'cancel')}
                            </button>
                          </div>
                        </div>
                      )}
                      {signOutResult[row.id] && (
                        <p
                          className={`text-xs mt-1 ${signOutResult[row.id] === 'done' ? 'text-green-700' : 'text-destructive'}`}
                          data-testid={`staff-signout-result-${row.id}`}
                        >
                          {st(lang, signOutResult[row.id] === 'done' ? 'signOutEverywhereDone' : 'signOutEverywhereFailed')}
                        </p>
                      )}

                      {!isOnline && (
                        <p className="text-xs text-amber-600 mt-1" data-testid={`staff-switch-offline-${row.id}`}>
                          {st(lang, 'needsConnection')}
                        </p>
                      )}
                    </div>
                  )}

                  {row.canManage && (
                    <div className="mt-3" data-testid={`staff-roles-section-${row.id}`}>
                      {rolesOpenId !== row.id && (
                        <button
                          type="button"
                          onClick={() => openRoles(row)}
                          disabled={!isOnline}
                          data-testid={`staff-edit-roles-${row.id}`}
                          className="text-sm font-semibold text-foreground hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed min-h-[44px]"
                        >
                          {st(lang, 'actionEditRoles')}
                        </button>
                      )}
                      {rolesOpenId === row.id && (
                        <form
                          className="mt-2 space-y-2"
                          data-testid={`staff-roles-form-${row.id}`}
                          onSubmit={(e) => {
                            e.preventDefault()
                            if (!rolesForm.submitting && isOnline && rolesState === 'ready') void submitRoles(row)
                          }}
                        >
                          {rolesForm.error && (
                            <div
                              className="rounded-md bg-destructive/10 border border-destructive/30 px-3 py-2 text-xs text-destructive"
                              data-testid={`staff-roles-error-${row.id}`}
                            >
                              {rolesForm.error}
                            </div>
                          )}
                          {renderRolePicker(`staff-roles-${row.id}`, selectedRoleIds(row), (id) =>
                            setRolesForm((prev) => ({ ...prev, roleIds: toggleId(selectedRoleIds(row), id) })),
                          )}
                          <div className="flex items-center gap-2">
                            <button
                              type="submit"
                              disabled={rolesForm.submitting || !isOnline || rolesState !== 'ready'}
                              data-testid={`staff-roles-submit-${row.id}`}
                              className="rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors min-h-[44px]"
                            >
                              {rolesForm.submitting ? st(lang, 'savingRoles') : st(lang, 'submitRoles')}
                            </button>
                            <button
                              type="button"
                              onClick={() => setRolesOpenId(null)}
                              disabled={rolesForm.submitting}
                              className="text-sm text-muted-foreground hover:text-foreground min-h-[44px]"
                            >
                              {st(lang, 'cancel')}
                            </button>
                          </div>
                        </form>
                      )}
                    </div>
                  )}

                  {successMsg[row.id] && (
                    <p className="mt-2 text-sm text-green-700" data-testid={`staff-success-${row.id}`}>
                      {successMsg[row.id]}
                    </p>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </main>
    </div>
  )
}
